import React from 'react';
import { Card, Form, Tabs, Button, Space, Spin, message } from 'antd';
import { SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from './ManagementForm.module.css';

interface TabItem {
  key: string;
  label: string;
  children: React.ReactNode;
}

interface ManagementFormProps {
  title: string;
  isCreating?: boolean;
  isLoading?: boolean;
  isSaving?: boolean;
  form: any; // Ant Design Form instance
  tabs?: TabItem[];
  children?: React.ReactNode; // For simple forms without tabs
  onSave: (values: any) => Promise<void> | void;
  onCancel: () => void;
  onDelete?: () => Promise<void> | void;
  showDelete?: boolean;
  className?: string;
}

/**
 * Standardized form component for management pages
 * 
 * Provides consistent form layout with:
 * - Optional tabs for complex forms
 * - Standardized action buttons
 * - Loading states
 * - Error handling
 */
export const ManagementForm: React.FC<ManagementFormProps> = ({
  title,
  isCreating = false,
  isLoading = false,
  isSaving = false,
  form,
  tabs,
  children,
  onSave,
  onCancel,
  onDelete,
  showDelete = false,
  className = '',
}) => {
  const handleSubmit = async (values: any) => {
    await onSave(values);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    try {
      await onDelete();
    } catch (error: any) {
      void message.error(error.message || 'Failed to delete');
    }
  };

  const formContent = tabs ? (
    <Tabs
      items={tabs}
      className={styles.managementFormTabs}
      destroyOnHidden={false}
    />
  ) : (
    <div className={styles.managementFormContent}>
      {children}
    </div>
  );

  return (
    <Card
      className={`${styles.managementFormCard} ${className}`}
      title={
        <div className={styles.managementFormHeader}>
          <span>{title}</span>
        </div>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          className={styles.managementForm}
          onFinish={handleSubmit}
        >
          {formContent}
          
          <Form.Item className={styles.managementFormActions}>
            <Space>
              {showDelete && !isCreating && onDelete && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              )}
              <Button
                icon={<CloseOutlined />}
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={isSaving}
              >
                {isCreating ? 'Create' : 'Save'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

